import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ChartLineUp, CurrencyEur, Wine } from "@phosphor-icons/react";

const MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

/** Live widget for the Patron dashboard: CA split Danseuses vs Boissons. */
export default function BusinessSplitWidget() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthLabel = useMemo(() => `${MONTHS[month - 1]} ${year}`, [month, year]);

  const load = () => {
    setLoading(true);
    api
      .get("/stats/business-split", { params: { year, month } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year, month]);

  const rD = data?.ratio_danseuses ?? 0;
  const rB = data?.ratio_boissons ?? 0;

  return (
    <div className="surface p-5" data-testid="business-split-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ChartLineUp size={18} color="#fbbf24" weight="fill" />
            <h3 className="font-display text-lg font-medium">Répartition du CA</h3>
          </div>
          <div className="text-xs text-[#71717a] capitalize">
            {monthLabel} • {data?.days_saisis ?? 0} jour(s) saisi(s)
          </div>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="split-month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="bg-[#121217] border border-[#27272a] text-white rounded-md px-2 py-1 h-9 text-sm capitalize"
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <Input
            data-testid="split-year"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="bg-[#121217] border-[#27272a] h-9 w-[90px] text-sm"
          />
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            className="h-9 border-[#27272a] text-white hover:bg-[#1a1a21]"
            data-testid="split-refresh"
          >
            {loading ? "..." : "↻"}
          </Button>
        </div>
      </div>

      {/* Big CA total */}
      <div className="mb-5">
        <div className="label-xs">CA total du mois</div>
        <div
          data-testid="split-total-ca"
          className="font-display text-4xl font-light tracking-tight mt-1"
          style={{ color: "#fbbf24" }}
        >
          {fmtMoney(data?.total_ca || 0)}
        </div>
      </div>

      {/* Split bar */}
      <div className="mb-4">
        <div className="h-3 w-full rounded-full overflow-hidden flex bg-[#27272a]">
          <div
            data-testid="split-bar-danseuses"
            style={{ width: `${rD}%`, background: "#fbbf24" }}
            className="h-full transition-all"
            title={`Danseuses ${rD}%`}
          />
          <div
            data-testid="split-bar-boissons"
            style={{ width: `${rB}%`, background: "#34d399" }}
            className="h-full transition-all"
            title={`Boissons ${rB}%`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SplitCard
          icon={CurrencyEur}
          color="#fbbf24"
          label="CA Danseuses (billets + jetons)"
          value={fmtMoney(data?.total_ca_danseuses || 0)}
          ratio={rD}
          testId="split-danseuses"
          extras={[
            { label: "Dû aux danseuses", value: fmtMoney(data?.total_du_danseuses || 0), color: "#fbbf24" },
            { label: "Déjà payé", value: fmtMoney(data?.total_paye_danseuses || 0), color: "#34d399" },
            {
              label: "Reste à payer",
              value: fmtMoney(data?.total_reste_danseuses || 0),
              color: (data?.total_reste_danseuses || 0) > 0 ? "#f87171" : "#a1a1aa",
            },
          ]}
        />
        <SplitCard
          icon={Wine}
          color="#34d399"
          label="CA Boissons / Reste"
          value={fmtMoney(data?.total_boissons_autre || 0)}
          ratio={rB}
          testId="split-boissons"
          extras={[
            { label: "Charges du mois", value: fmtMoney(data?.total_charges || 0), color: "#f87171" },
          ]}
          hint="CA total − CA généré par les danseuses"
        />
      </div>
    </div>
  );
}

function SplitCard({ icon: Icon, color, label, value, ratio, extras = [], hint, testId }) {
  return (
    <div className="p-4 rounded-md border border-[#27272a] bg-[#09090b]" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} color={color} weight="fill" />
        <div className="label-xs">{label}</div>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="font-display text-2xl md:text-3xl font-light" style={{ color }}>
          {value}
        </div>
        <div className="text-sm text-[#a1a1aa]">{ratio}%</div>
      </div>
      {hint && <div className="text-[11px] text-[#71717a] italic mt-1">{hint}</div>}
      {extras.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#27272a] space-y-1.5">
          {extras.map((e) => (
            <div key={e.label} className="flex items-center justify-between text-xs">
              <span className="text-[#a1a1aa]">{e.label}</span>
              <span className="font-mono" style={{ color: e.color }}>{e.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
